import type { Changelog, ChangelogRecord, Release } from '~~/layers/changelog/shared/types/changelog';
import { useChangelogDb } from './useChangelogDb';
import { diffReleases, nextChangelogState } from '../utils/changelogDiff';
import { filterChangelogForTenant } from '../utils/changelogFilter';
import { compareVersions } from '../utils/semver';

// Explicit relative imports of the layer's own utils/composable (rather than the
// ambient auto-import) so the dependency graph of this orchestrator is legible and
// the boundary is intra-layer only.

interface ChangelogUiState {
	currentVersion: string;
	lastSeenVersion: string | null;
	popoverOpen: boolean;
	popoverDismissed: boolean;
	modalOpen: boolean;
	view: 'diff' | 'history';
	releases: Release[];
	fetched: boolean;
}

// The single orchestrator behind all three surfaces (footer link, popover, modal).
// Shared reactive state lives in useState('lcp-changelog') so every surface reads
// the same instance; the client detection plugin calls detect() once on load.
export function useChangelog() {
	const config = useRuntimeConfig();
	const appVersion = String(config.public.version ?? '');
	const db = useChangelogDb();

	// Tenant relevance (see AGENTS: this is the changelog's one reach into the config
	// layer, foundation-to-foundation). Read the public surface only.
	const { productType, portalType, isFeatureEnabled } = useCustomerConfig();

	const state = useState<ChangelogUiState>('lcp-changelog', () => ({
		currentVersion: appVersion,
		lastSeenVersion: null,
		popoverOpen: false,
		popoverDismissed: true,
		modalOpen: false,
		view: 'diff',
		releases: [],
		fetched: false,
	}));

	// Lazy one-shot fetch of the static changelog.json. A missing/broken file
	// degrades to an empty release list, never a thrown error or broken surface.
	async function fetchReleases(): Promise<Release[]> {
		if (state.value.fetched) return state.value.releases;
		try {
			const data = await $fetch<Changelog>('/changelog.json');
			state.value.releases = Array.isArray(data?.releases) ? data.releases : [];
		} catch {
			state.value.releases = [];
		}
		state.value.fetched = true;
		return state.value.releases;
	}

	// The releases relevant to the active tenant, via the same pure filter the build uses,
	// so dev/SSR render exactly what a targeted static bundle would ship. A release with
	// nothing relevant collapses to a generic entry (never blank), which also keeps
	// hasContent and the footer link stable on the running version.
	const relevantReleases = computed(
		() =>
			filterChangelogForTenant(
				{ releases: state.value.releases },
				{
					productType: productType.value,
					portalType: portalType.value,
					isFeatureEnabled,
				},
			).releases,
	);

	const sortedReleases = computed(() =>
		[...relevantReleases.value].sort((a, b) => compareVersions(b.version, a.version)),
	);

	const diffWindow = computed(() =>
		diffReleases(
			relevantReleases.value,
			state.value.lastSeenVersion,
			state.value.currentVersion,
		).sort((a, b) => compareVersions(b.version, a.version)),
	);

	// The releases the modal renders, per the active view.
	const visibleReleases = computed(() =>
		state.value.view === 'history' ? sortedReleases.value : diffWindow.value,
	);

	// The footer link and popover only make sense once the changelog actually
	// contains the running version; otherwise the surfaces stay hidden (graceful
	// degradation for a stale or unfetched changelog.json).
	const hasContent = computed(() =>
		relevantReleases.value.some((r) => compareVersions(r.version, state.value.currentVersion) <= 0),
	);

	const diffHeading = computed(() => {
		const current = state.value.currentVersion;
		const lastSeen = state.value.lastSeenVersion;
		if (!lastSeen) return `What's new in v${current}`;
		const n = diffWindow.value.length;
		const releaseWord = n === 1 ? 'release' : 'releases';
		return `v${lastSeen} → v${current} · ${n} ${releaseWord} since your last visit`;
	});

	// v-model:open bindings for the Nuxt UI popover/modal.
	const popoverOpen = computed({
		get: () => state.value.popoverOpen,
		set: (v: boolean) => (state.value.popoverOpen = v),
	});
	const modalOpen = computed({
		get: () => state.value.modalOpen,
		set: (v: boolean) => (state.value.modalOpen = v),
	});
	const view = computed({
		get: () => state.value.view,
		set: (v: 'diff' | 'history') => (state.value.view = v),
	});

	// Persist the record with popoverDismissed=true; used by both dismiss paths so
	// the popover stays gone across reloads.
	async function persistDismissed(): Promise<void> {
		state.value.popoverDismissed = true;
		const record: ChangelogRecord = {
			id: 'state',
			lastSeenVersion: state.value.lastSeenVersion,
			currentVersion: state.value.currentVersion,
			popoverDismissed: true,
		};
		await db.write(record);
	}

	// Runs once on client load (from the .client detection plugin). Reads the stored
	// record, computes the next state, writes it back, then offers the popover only
	// when the client landed on a new version AND the diff window is non-empty.
	async function detect(): Promise<void> {
		if (!import.meta.client) return;
		const stored = await db.read();
		const next = nextChangelogState(stored, appVersion);
		await db.write(next);

		state.value.currentVersion = next.currentVersion;
		state.value.lastSeenVersion = next.lastSeenVersion;
		state.value.popoverDismissed = next.popoverDismissed;

		if (next.popoverDismissed) return;

		await fetchReleases();
		state.value.popoverOpen = diffWindow.value.length > 0;
	}

	async function openModal(nextView: 'diff' | 'history' = 'diff'): Promise<void> {
		await fetchReleases();
		state.value.view = nextView;
		state.value.modalOpen = true;
	}

	function closeModal(): void {
		state.value.modalOpen = false;
	}

	function setView(nextView: 'diff' | 'history'): void {
		state.value.view = nextView;
	}

	// The X: dismiss the popover without opening the modal.
	async function dismissPopover(): Promise<void> {
		state.value.popoverOpen = false;
		await persistDismissed();
	}

	// "View changes": close the popover, mark it dismissed, open the modal on the diff.
	async function viewChanges(): Promise<void> {
		state.value.popoverOpen = false;
		await persistDismissed();
		await openModal('diff');
	}

	return {
		state,
		popoverOpen,
		modalOpen,
		view,
		sortedReleases,
		diffWindow,
		visibleReleases,
		hasContent,
		diffHeading,
		detect,
		fetchReleases,
		openModal,
		closeModal,
		setView,
		dismissPopover,
		viewChanges,
	};
}
